import { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';

import { z } from 'zod';

import { SignupDataType, usePostSignupApi } from '@/api/auth';
import Field from '@/components/common/Field/Field';
import InputSelect from '@/components/common/Input/InputSelect/InputSelect';
import InputText from '@/components/common/Input/InputText/InputText';
import AddIcon from '@/components/Icon/AddIcon';
import CloseIcon from '@/components/Icon/CloseIcon';
import { degreeOptions, formattedCountryOptions } from '@/constants/option';

import * as S from './style';
import { useSignUpStore } from '../SignUpContext/SignUpContext';

interface RegisterField {
	educations: { school: string; major: string; degree: string; year: string; country: string }[]; // "name"을 필드로 갖는 배열
}

const EducationInfoForm = () => {
	const { setStep, updateEducationInfo, getInfo, educationInfo, setCurrentStep } = useSignUpStore();
	const { mutateAsync: Signup } = usePostSignupApi();

	const userRegisterSchema = z.object({
		educations: z.array(
			z.object({
				school: z.string().min(1, '학교를 입력해 주세요.'),
				major: z.string().min(1, '전공을 입력해 주세요.'),
				degree: z.string().min(1, '학위를 선택해 주세요.'),
				year: z.string().min(1, '졸업년도를 입력해 주세요.'),
				country: z.string().min(1, '국가를 선택해 주세요.'),
			})
		),
	});
	const {
		register,
		control,
		watch,
		handleSubmit,
		setValue,
		formState: { errors },
	} = useForm<RegisterField>({
		resolver: zodResolver(userRegisterSchema),
		defaultValues: {
			educations: [{ school: '', major: '', degree: '', year: '', country: '대한민국' }], // 초기값을 배열로 설정
		},
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'educations',
	});

	const onValidateData = (_: RegisterField) => {
		append({ school: '', major: '', degree: '', year: '', country: '대한민국' });
	};
	const onSubmit = async (data: RegisterField) => {
		updateEducationInfo(data.educations);
		const allData = getInfo();

		const params: SignupDataType = {
			member_name: allData.basicInfo.name,
			member_en_name: allData.basicInfo.enName,
			member_birth_year: allData.basicInfo.birth.year,
			member_birth_month: allData.basicInfo.birth.month,
			member_birth_date: allData.basicInfo.birth.day,
			member_gender: allData.basicInfo.gender,
			member_cell: allData.basicInfo.cell.replace(/-/g, ''),
			member_id: allData.basicInfo.id,
			member_password: allData.basicInfo.password,
			member_re_password: allData.basicInfo.passwordCheck,
			member_zipcode: allData.basicInfo.address.zipcode,
			member_address: allData.basicInfo.address.address,
			member_address_detail: allData.basicInfo.address.detailAddress,
			member_email: `${allData.basicInfo.email.front}@${allData.basicInfo.email.back}`,
			member_company_name: allData.companyInfo.company_name ?? '',
			member_company_en_name: allData.companyInfo.company_name_en ?? '',
			member_position: allData.companyInfo.position ?? '',
			member_md: allData.companyInfo.md ?? '',
			member_company_tel: allData.companyInfo.phone ?? '',
			member_company_fax: allData.companyInfo.fax ?? '',
			member_company_zipcode: allData.companyInfo.zipcode ?? '',
			member_company_address: allData.companyInfo.address ?? '',
			member_company_address_detail: allData.companyInfo.detailAddress ?? '',
			member_edu: allData.educationInfo.map((edu) => `${edu.school}♩${edu.major}♩${edu.degree}♩${edu.year}♩${edu.country}`).join('♫'),
		};

		try {
			const res = await Signup(params);
			if (res.data.success) {
				setStep('회원가입 완료');
				setCurrentStep('회원가입 완료');
			}
		} catch (error) {
			console.error('❌ Signup API 요청 실패:', error);
		}
	};
	const educations = watch('educations');

	// 모든 입력 값이 다 채워졌는지 검사하는 함수
	const isAllFieldsFilled = educations.every((edu) => edu.school && edu.major && edu.degree && edu.year && edu.country);
	useEffect(() => {
		const hasInfo = educationInfo.some((edu) => edu.school || edu.major || edu.degree || edu.year || edu.country);

		if (hasInfo) {
			const newEducations = educationInfo.map((item) => ({
				school: item.school ?? '',
				major: item.major ?? '',
				degree: item.degree ?? '',
				year: item.year ?? '',
				country: item.country ?? '',
			}));
			// 첫 번째 데이터는 setValue로 직접 설정
			if (newEducations.length > 0) {
				setValue('educations', newEducations);
			}
		}
	}, [educationInfo, setValue]);
	return (
		<S.FormContainer onSubmit={handleSubmit(onSubmit)}>
			<S.Container>
				<S.Danger>*필수 입력 값</S.Danger>

				{fields.map((f, index) => (
					<S.FieldBox key={f.id}>
						<S.Title>
							학력정보 {index + 1 > 9 ? '' : '0'}
							{index + 1}
							{index + 1 > 1 && <CloseIcon onClose={() => remove(index)} />}
						</S.Title>
						<S.FlexRow>
							<Field title="*출신학교" error={errors.educations?.[index]?.school?.message ?? ''}>
								<InputText {...register(`educations.${index}.school`)} isError={!!errors.educations?.[index]?.school?.message} placeholder="학교 입력" />
							</Field>
							<Field title="*전공" error={errors.educations?.[index]?.major?.message ?? ''}>
								<InputText isError={!!errors.educations?.[index]?.major?.message} {...register(`educations.${index}.major`)} placeholder="전공 입력" />
							</Field>
						</S.FlexRow>
						<Field title="*학위" error={errors.educations?.[index]?.degree?.message ?? ''}>
							<S.SelectBox>
								<InputSelect
									defaultValue="선택"
									options={degreeOptions}
									selectedOption={watch(`educations.${index}.degree`)}
									setSelectedOption={(value) => setValue(`educations.${index}.degree`, value, { shouldValidate: true })}
								/>
							</S.SelectBox>
						</Field>
						<Field title="*졸업년도" error={errors.educations?.[index]?.year?.message ?? ''}>
							<S.SelectBox>
								<InputText
									isError={!!errors.educations?.[index]?.year?.message}
									input={(e) => {
										e.target.value = e.target.value.replace(/[^0-9]/g, ''); // 숫자만 허용
										setValue(`educations.${index}.year`, e.target.value, { shouldValidate: true });
									}}
									value={watch(`educations.${index}.year`)}
									maxLength={4}
									placeholder="졸업년도 입력"
								/>
							</S.SelectBox>
						</Field>
						<Field title="*국가" error={errors.educations?.[index]?.country?.message ?? ''}>
							<S.SelectBox>
								<InputSelect
									options={formattedCountryOptions}
									selectedOption={watch(`educations.${index}.country`)}
									setSelectedOption={(value) => setValue(`educations.${index}.country`, value, { shouldValidate: true })}
								/>
							</S.SelectBox>
						</Field>
					</S.FieldBox>
				))}
				<S.AddButton type="button" onClick={handleSubmit(onValidateData)}>
					<AddIcon />
					추가
				</S.AddButton>
			</S.Container>
			<S.GuideBox>
				<S.GuideTextStrong>본 학회 학생회원 기준 : 학부생 ~ 석사과정생까지</S.GuideTextStrong>
				<S.GuideText>
					Part time은 일반회원으로 되며, 석박통합의 경우 5학기부터는 일반회원입니다.
					<br />
					<br />
					[일반회원 → 학생회원으로 변경 절차]
					<br />
					회원가입 후 로그인 → My → 재학인증 → 재학증명서 업로드 → 등록
					<br />
					추후 학회 사무국에서 재학증명서 확인 후 학생회원으로 변경진행
					<br />
					(재학증명서 업로드 후 학회 사무국 scsk1968@naver.com 메일 발송)
				</S.GuideText>
			</S.GuideBox>
			<S.NextButton type="submit" disabled={!isAllFieldsFilled}>
				다음
			</S.NextButton>
		</S.FormContainer>
	);
};

export default EducationInfoForm;
